import React from "react";
import FormGenerator from "components/form-generator/FormGenerator";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import {
  setBreadCrumbs,
  setCurrent,
  setNavigationComponents,
} from "features/navigation/navigationSlice";
import { MultiSelect } from "react-multi-select-component";
import customAxios from "middleware/customAxios";
import Card from "ui/Card";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export default function UsersEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation(); // kept in case we want to use in future

  // Canonical user row used to prefill form; always fetched from API by id
  const [userRow, setUserRow] = React.useState(null);

  const [roles, setRoles] = React.useState([]);
  const [outlets, setOutlets] = React.useState([]);
  const [selectedRoles, setSelectedRoles] = React.useState([]);
  const [selectedOutlets, setSelectedOutlets] = React.useState([]);
  const [username, setUsername] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    dispatch(
      setCurrent({
        name: "Edit User",
      })
    );
    dispatch(setNavigationComponents(null));
    // Breadcrumbs for edit page: Users > Edit User
    dispatch(
      setBreadCrumbs([
        { name: "Users", path: "/users" },
        { name: "Edit User", path: `/users/edit/${id}` },
      ])
    );
  }, [dispatch, id]);

  // Always fetch the full users list and find the matching user by id
  // so we have a consistent shape (including roles and userGroups).
  React.useEffect(() => {
    if (!id) return;
    customAxios.get("/admin/users").then((res) => {
      // customAxios already returns the response body.
      // For /admin/users, shape is { data: [users], columns: [...] }.
      const body = res || {};
      const users = Array.isArray(body.data) ? body.data : Array.isArray(body) ? body : [];
      const found = users.find((u) => String(u.id) === String(id));
      if (found) {
        setUserRow(found);
        setUsername(found.username || "");
      }
    });
  }, [id]);

  // Load roles and outlets, and preselect based on current user
  React.useEffect(() => {
    customAxios.get("/admin/roles").then((res) => {
      const roleOptions = (res?.data || res || []).map((role) => ({
        label: role.name,
        value: role.id,
      }));
      setRoles(roleOptions);
      if (userRow?.roles) {
        const currentRoleIds = userRow.roles
          .map((r) => r.roleId || r.role?.id)
          .filter(Boolean);
        setSelectedRoles(
          roleOptions.filter((opt) => currentRoleIds.includes(opt.value))
        );
      }
    });

    customAxios.get("/admin/user-groups").then((res) => {
      const outletOptions = (res?.data || res || []).map((outlet) => ({
        label: outlet.name,
        value: outlet.id,
      }));
      setOutlets(outletOptions);
      if (userRow?.userGroups) {
        const currentGroupIds = userRow.userGroups
          .map((ug) => ug.groupId || ug.group?.id)
          .filter(Boolean);
        setSelectedOutlets(
          outletOptions.filter((opt) => currentGroupIds.includes(opt.value))
        );
      }
    });
  }, [userRow]);

  const formData = {
    contents: [
      {
        fields: [
          {
            name: "firstName",
            displayName: "First Name",
            type: "field",
            inputType: "string",
            required: true,
          },
          {
            name: "lastName",
            displayName: "Last Name",
            type: "field",
            inputType: "string",
            required: true,
          },
          {
            name: "email",
            displayName: "Email",
            type: "field",
            inputType: "email",
            required: true,
          },
          {
            name: "phone",
            displayName: "Phone",
            type: "field",
            inputType: "string",
            required: true,
          },
          {
            name: "address",
            displayName: "Address",
            type: "textArea",
            inputType: "text",
            required: false,
          },
        ],
      },
    ],
    initialValues: {
      firstName: userRow?.userInfo?.firstName || "",
      lastName: userRow?.userInfo?.lastName || "",
      email: userRow?.userInfo?.email || "",
      phone: userRow?.userInfo?.phone || "",
      address: userRow?.userInfo?.address || "",
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string().required("First Name is required"),
      lastName: Yup.string().required("Last Name is required"),
      email: Yup.string().email("Email is invalid").required("Email is required"),
      phone: Yup.string().required("Phone is required"),
    }),
    onSubmit: async (values, { setErrors }) => {
      try {
        setLoading(true);
        const payload = {
          username,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          address: values.address,
          roles: selectedRoles.map((r) => r.value),
          outlets: selectedOutlets.map((o) => o.value),
        };
        await customAxios.put(`/admin/user/${id}`, payload);
        navigate("/users");
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          err.message ||
          "Failed to update user. Please try again.";
        alert(msg);
      } finally {
        setLoading(false);
      }
    },
  };

  // While loading the user row (e.g. on refresh) show nothing / rely on form defaults
  if (!userRow) {
    return (
      <div className="absolute top-0 w-full flex items-center justify-center h-full">
        <p>Loading user...</p>
      </div>
    );
  }

  return (
    <div className="absolute grid pb-40 gap-3 bg-slate-50 rounded-xl top-0 w-full">
      <Card
        heading={"User Information"}
        description={"Update the following details"}
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col space-y-2">
            <p className="text-[12px]">Username</p>
            <input
              type="text"
              className="border border-gray-300 rounded-md p-2"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <Card
        heading={"User Details"}
        description={"Update name, email, phone, address"}
      >
        <FormGenerator
          grids={2}
          modal={true}
          buttonSubmit={{ title: loading ? "Saving..." : "Save" }}
          formData={formData}
        />
      </Card>

      <Card
        heading={"Roles and Outlets"}
        description={"Update the user roles and outlets"}
      >
        <div className="flex flex-col space-y-3">
          <p>Select Roles</p>
          <MultiSelect
            style={{ height: 5 }}
            options={roles}
            value={selectedRoles}
            onChange={setSelectedRoles}
            labelledBy="Select"
          />
          <p>Select Outlets</p>
          <MultiSelect
            style={{ height: 5 }}
            options={outlets}
            value={selectedOutlets}
            onChange={setSelectedOutlets}
            labelledBy="Select Outlet"
          />
        </div>
      </Card>
    </div>
  );
}
